import { useFetcher, useLoaderData, useNavigate, useRevalidator } from '@remix-run/react';
import { useEffect, useRef, useState } from 'react';
import { ROUND_UP, XRD_ICON, getIconUrl, onIconError } from '~/lib/util';
import { LoaderFunctionArgs, json, redirect } from '@remix-run/cloudflare';
import AccountSelector from '~/components/account-selector';
import { useRadix } from '~/rdt/radixProvider';
import TransactionButton from '~/components/transaction-button';
import { getPairs } from '~/lib/models/pair';
import { SelectToken } from '~/lib/models/tokens';
import { Account } from '@radixdlt/radix-dapp-toolkit';
import { loader as walletLoader } from '~/routes/wallet.$account';
import BigNumber from 'bignumber.js';
import { IGNITION_LOCKUPS, POOL_TPES, generateOpenIgnitionManifest } from '~/lib/ignition';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { fetchIgnitionIncentiveAmountLeft, ignitionPrice } from '~/rdt/gateway';
import { setDbFromContext } from '~/lib/db';

dayjs.extend(localizedFormat);

export async function loader({ request, params, context }: LoaderFunctionArgs) {
   setDbFromContext(context);

   if (!params.baseToken) {
      throw redirect('/ignition');
   }

   const allPairs = await getPairs();
   const pair = allPairs.find((c) => c.Token.address == params.baseToken);

   const priceXRD = await ignitionPrice(params.baseToken);

   const amounts = await fetchIgnitionIncentiveAmountLeft();

   const amountLeft = amounts[POOL_TPES[params.baseToken]];

   return json({ pair, lockups: IGNITION_LOCKUPS, priceXRD, amountLeft });
}

export default function IgnitionDetail() {
   const { pair, lockups, priceXRD, amountLeft } = useLoaderData<typeof loader>();
   const { sendTransaction } = useRadix();
   const navigate = useNavigate();
   const revalidator = useRevalidator();
   const walletFetcher = useFetcher<typeof walletLoader>();

   const [account, setAccount] = useState<Account | null>(null);
   const [inputAmount, setInputAmount] = useState<string>('');
   const [balance, setBalance] = useState<string>('');
   const [error, setError] = useState<string>('');
   const [lockupPeriod, setLockupPeriod] = useState<number>(lockups.length -1);

   const [inputToken, setInputToken] = useState<SelectToken>(pair.Token);
   const inputRef = useRef<HTMLInputElement>(null);

   const [warning, setWarning] = useState<string | null>();

   useEffect(() => {
      setInputToken(pair?.Token);
      setInputAmount('');
      setLockupPeriod(lockups.length - 1);

      setBalance(walletFetcher.data?.filter((b) => b.address == pair?.Token.address)[0]?.amount || '0');

      inputRef?.current?.focus();
   }, [pair]);

   useEffect(() => {
      if (!walletFetcher.data) {
         return;
      }

      setBalance(walletFetcher.data?.filter((b) => b.address == inputToken.address)[0]?.amount || '0');
   }, [walletFetcher.data]);

   function setRelativeInputAmount(input: string) {
      setInputAmount(new BigNumber(balance).times(input).toFixed(inputToken.divisibility, ROUND_UP));
   }

   async function onAddLiquidity() {
      if (!account) {
         return;
      }

      if (!pair) {
         return;
      }

      const transactionManifest = generateOpenIgnitionManifest(pair, account.address, inputToken.address, new BigNumber(inputAmount), lockups[lockupPeriod].seconds);

      await sendTransaction(transactionManifest, `Add ${inputToken.symbol} as liquidity to DefiPlaza Ignition 🚀`);

      revalidator.revalidate();
      setInputAmount('');
      walletFetcher.load(`/wallet/${account.address}`);
   }

   function onAccount(account: Account) {
      setAccount(account);

      walletFetcher.load(`/wallet/${account.address}`);
   }

   return (
     <div className="bg-primary-800 flex w-1/2 flex-col space-y-4 rounded-xl p-8 text-white lg:rounded-2xl">
       {error ? <p className="shadow-xs text-md mt-2 rounded-md bg-red-800 px-4 py-1 text-center text-base font-medium text-white">{error}</p> : null}

       {!error ? (
         <>
           <AccountSelector mode="light" onAccount={onAccount}></AccountSelector>
           <section className="mt-8 flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
             <div className="flex items-center justify-between">
               <label htmlFor="tokenInput" className="text-sm text-purple-800">
                 Add
               </label>
               {walletFetcher.data ? (
                 <span className="w-60 truncate text-right text-sm font-normal text-black lg:text-right">Balance: {(Math.round(+balance * 1000000) / 1000000).toLocaleString()}</span>
               ) : null}
             </div>
             <div className="flex items-center justify-between space-x-4">
               <div className="hover:text-primary-500 focus:outline-3 focus:ring-primary-200 focus:border-primary-300 w-40 flex-none rounded-md py-2 text-left text-black focus:ring-1 sm:text-sm">
                 <span className="flex items-center">
                   <img src={getIconUrl(inputToken.iconUrl)} onError={(evt) => onIconError(evt)} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                   <span className="ml-3 block text-xl font-medium">{inputToken.symbol}</span>
                   {/* <span className="ml-3 text-xs text-gray-500 truncate">{token.name}</span> */}
                 </span>
               </div>
               <input
                 ref={inputRef}
                 type="text"
                 value={inputAmount ? inputAmount : ""}
                 onChange={(evt) => setInputAmount(evt.target.value)}
                 placeholder="0.00"
                 autoComplete="off"
                 spellCheck="false"
                 className="m-0 w-40 flex-auto border-none bg-transparent p-0 text-right text-4xl text-black placeholder:text-purple-100 dark:placeholder:text-purple-800/80"
               />
             </div>
             {walletFetcher.data && +balance > 0 ? (
               <>
                 <p className="text-primary-500 mt-6 text-right text-sm font-normal">{Math.round((+inputAmount / +balance) * 100)}%</p>
                 <input
                   id="steps-range"
                   type="range"
                   min="0"
                   max="1"
                   value={new BigNumber(inputAmount || 0).div(balance).toFixed(inputToken.divisibility, ROUND_UP) || 0}
                   onChange={(evt: React.ChangeEvent<HTMLInputElement>) => setRelativeInputAmount((evt.target as HTMLInputElement).value)}
                   step="0.01"
                   className="bg-primary-100 ring-primary-300 mb-3 mt-2 h-2 w-full cursor-pointer appearance-none rounded-lg"
                 />

                 <div className="flex w-full items-center justify-between space-x-1">
                   <button
                     type="button"
                     onClick={() => setRelativeInputAmount("0")}
                     className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                   >
                     0%
                   </button>
                   <button
                     type="button"
                     onClick={() => setRelativeInputAmount("0.25")}
                     className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                   >
                     25%
                   </button>
                   <button
                     type="button"
                     onClick={() => setRelativeInputAmount("0.5")}
                     className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                   >
                     50%
                   </button>
                   <button
                     type="button"
                     onClick={() => setRelativeInputAmount("0.75")}
                     className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                   >
                     75%
                   </button>
                   <button
                     type="button"
                     onClick={() => setRelativeInputAmount("1")}
                     className="text-primary-700 dark:text-primary-500 hover:text-primary-200 dark:hover:text-primary-300 border-primary-500 hover:border-primary-300 rounded-md border px-2 text-xs lining-nums"
                   >
                     100%
                   </button>
                 </div>
               </>
             ) : null}
           </section>

           {+inputAmount * priceXRD > amountLeft ? (
             <p className="shadow-xs text-md mt-2 rounded-md bg-red-800 px-4 py-1 text-center text-base font-medium text-white">There is only {Math.round(amountLeft)} XRD left in incentives.</p>
           ) : null}
           <section className="mt-8 flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
             <div className="flex items-center justify-between">
               <label htmlFor="tokenInput" className="text-sm text-purple-800">
                 Lockup period
               </label>
             </div>

             <p className="text-primary-500 text-right text-sm font-normal">{lockups[lockupPeriod].months} months</p>

             <input
               id="steps-range"
               type="range"
               min="0"
               max={lockups.length - 1}
               value={lockupPeriod}
               onChange={(evt: React.ChangeEvent<HTMLInputElement>) => setLockupPeriod((evt.target as HTMLInputElement).value)}
               step="1"
               className="bg-primary-100 ring-primary-300 mb-3 mt-2 h-2 w-full cursor-pointer appearance-none rounded-lg"
             />
             <p className="text-primary-500 text-right text-sm font-normal">Reward: {Math.round(lockups[lockupPeriod].reward * 1000) / 10}%</p>

             <p className="mt-5 text-black">Your liquidity will be locked until {dayjs().add(lockups[lockupPeriod].seconds, "s").format("lll")}.</p>
           </section>
           <section className="mt-8 flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
             <div className="flex items-center justify-between">
               <label htmlFor="tokenInput" className="text-sm text-purple-800">
                 Immediate Reward
               </label>
             </div>
             <div className="flex items-center justify-between space-x-4">
               <div className="hover:text-primary-500 focus:outline-3 focus:ring-primary-200 focus:border-primary-300 w-40 flex-none rounded-md py-2 text-left text-black focus:ring-1 sm:text-sm">
                 <span className="flex items-center">
                   <img src={XRD_ICON} onError={(evt) => onIconError(evt)} alt="" className="h-8 w-8 shrink-0 rounded-full" />
                   <span className="ml-3 block text-xl font-medium">XRD</span>
                   {/* <span className="ml-3 text-xs text-gray-500 truncate">{token.name}</span> */}
                 </span>
               </div>
               <input
                 type="text"
                 value={inputAmount ? parseFloat(inputAmount) * lockups[lockupPeriod].reward * priceXRD : "0"}
                 placeholder="0.00"
                 autoComplete="off"
                 spellCheck="false"
                 className="m-0 w-40 flex-auto border-none bg-transparent p-0 text-right text-4xl text-black placeholder:text-purple-100 dark:placeholder:text-purple-800/80"
                 readOnly={true}
               />
             </div>
           </section>
           {warning ? <p className="shadow-xs text-md mt-2 rounded-md bg-red-800 px-4 py-1 text-center text-base font-medium text-white">{warning}</p> : null}
           <TransactionButton title="Add Liquidity to Ignition" onClick={onAddLiquidity} />
         </>
       ) : null}
     </div>
   );
}
